import { Injectable } from '@angular/core';
import { FieldApiMetadata, ButtonConfig, AttributeValueDetail } from '../../public-form/models/field-api-config.model';
import { FormGroup } from '@angular/forms';
import { CustomAttibute } from '../../contact-setting/models/custom-attibute.model';
import { CustomAttributeTypeEnum } from '../constant/CustomAttributeTypeEnum';

/**
 * Helper class to handle field API configuration functionalities
 */
@Injectable({
  providedIn: 'root'
})
export class FieldApiConfigHelper {

  constructor() { }

  /**
   * Process the field API metadata response and convert to a Map
   * @param fieldApiMetadataList Array of field API metadata
   * @returns Map with fieldId as key and metadata as value
   */
  public processFieldApiMetadata(fieldApiMetadataList: FieldApiMetadata[]): Map<number, FieldApiMetadata> {
    const fieldApiMetadataMap = new Map<number, FieldApiMetadata>();

    if (!fieldApiMetadataList || !Array.isArray(fieldApiMetadataList)) {
      return fieldApiMetadataMap;
    }

    // Convert array to Map with fieldId as key
    fieldApiMetadataList.forEach(metadata => {
      if (metadata && metadata.fieldId) {
        fieldApiMetadataMap.set(metadata.fieldId, metadata);
      }
    });

    return fieldApiMetadataMap;
  }

  /**
   * Get button configuration for a specific field
   * @param fieldId The field ID to get button configuration for
   * @param fieldApiMetadataMap Map of field API metadata
   * @returns Object with button visibility and text, or null if no configuration found
   */
  public getButtonConfigForField(fieldId: number, fieldApiMetadataMap: Map<number, FieldApiMetadata>): { isVisible: boolean; buttonText: string } | null {
    if (!fieldId || !fieldApiMetadataMap.has(fieldId)) {
      return null;
    }

    const fieldMetadata = fieldApiMetadataMap.get(fieldId);
    const buttonConfig = fieldMetadata?.buttonConfig;

    if (!buttonConfig) {
      return { isVisible: false, buttonText: '' };
    }

    // Check if button should be visible (show field is "true" as string)
    const isVisible = buttonConfig.show === 'true';
    const buttonText = isVisible ? (buttonConfig.text || 'Verify') : '';

    return {
      isVisible,
      buttonText
    };
  }

  /**
   * Build attribute values for API execution request
   * @param customAttributes Array of custom attributes
   * @param fieldIds Array of field IDs to extract values for
   * @param formGroup The FormGroup containing the field values
   * @returns Array of AttributeValueDetail objects
   */
  public buildAttributeValuesForApi(
    customAttributes: CustomAttibute[],
    fieldIds: number[],
    formGroup: FormGroup
  ): AttributeValueDetail[] {
    const attributeValues: AttributeValueDetail[] = [];

    fieldIds.forEach(fieldId => {
      // Find the custom attribute with matching ID
      const customAttribute = customAttributes.find(attr => attr.id === fieldId);

      if (customAttribute && customAttribute.combinedAttributeName) {
        const controlName = customAttribute.combinedAttributeName;
        const control = formGroup.get(controlName);

        if (control) {
          const rawValue = control.value;

          // Convert value to string based on attribute type
          let fieldValue = '';
          if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
            if (customAttribute.attributeType.id === CustomAttributeTypeEnum.DATE) {
              fieldValue = this.formatDate(rawValue);
            } else {
              fieldValue = String(rawValue);
            }
          }

          attributeValues.push({
            attributeId: fieldId,
            parentDataListId: null,
            attributeValue: fieldValue
          });
        }
      }
    });

    return attributeValues;
  }

  /**
   * Validate that all required fields have values
   * @param customAttributes Array of custom attributes
   * @param fieldIds Array of field IDs to validate
   * @param formGroup The FormGroup containing the field values
   * @returns Object with isValid flag and array of invalid field names
   */
  public validateRequiredFields(
    customAttributes: CustomAttibute[],
    fieldIds: number[],
    formGroup: FormGroup
  ): { isValid: boolean; invalidFieldNames: string[] } {
    const invalidFieldNames: string[] = [];

    fieldIds.forEach(fieldId => {
      const customAttribute = customAttributes.find(attr => attr.id === fieldId);

      if (customAttribute && customAttribute.combinedAttributeName) {
        const controlName = customAttribute.combinedAttributeName;
        const control = formGroup.get(controlName);

        if (control) {
          const rawValue = control.value;
          const hasValue = rawValue !== null && rawValue !== undefined && rawValue !== '';

          if (!hasValue) {
            invalidFieldNames.push(customAttribute.attributeName);
          }
        }
      }
    });

    return {
      isValid: invalidFieldNames.length === 0,
      invalidFieldNames
    };
  }

  /**
   * Auto-fill form fields from field mappings
   * @param fieldMappings Map of fieldId to value
   * @param customAttributes Array of custom attributes
   * @param formGroup The FormGroup to update
   */
  public autoFillFieldsFromMappings(
    fieldMappings: { [fieldId: number]: string },
    customAttributes: CustomAttibute[],
    formGroup: FormGroup
  ): void {
    if (!fieldMappings || Object.keys(fieldMappings).length === 0) {
      return;
    }

    // Process each field mapping
    Object.entries(fieldMappings).forEach(([fieldIdStr, value]) => {
      const fieldId = parseInt(fieldIdStr, 10);

      // Find corresponding combined field name from custom attributes list
      const customAttribute = customAttributes.find(attr => attr.id === fieldId);

      if (!customAttribute || !customAttribute.combinedAttributeName) {
        return;
      }

      const controlName = customAttribute.combinedAttributeName;

      // Find the respective form control from the form group
      const control = formGroup.get(controlName);

      if (!control) {
        return;
      }

      // Put the value in the form control if value exists
      if (value !== undefined) {
        control.setValue(value);
        control.updateValueAndValidity();
        control.markAsTouched();
      }
    });
  }

  /**
   * Check if there are any fields with pending or failed validations
   * @param customAttributesList List of custom attributes
   * @param formGroup The form group to check
   * @param fieldValidationStatus Validation status map
   * @param fieldApiMetadataMap Map of field API metadata
   * @returns Array of field names that have pending or failed validations
   */
  public checkPendingFieldValidations(
    customAttributesList: CustomAttibute[],
    formGroup: FormGroup,
    fieldValidationStatus: { [key: number]: { isValidated: boolean; isSuccess: boolean } },
    fieldApiMetadataMap: Map<number, FieldApiMetadata>
  ): string[] {
    const pendingFields: string[] = [];

    // Get all field IDs that have API configurations
    const fieldIdsWithApiConfig = Array.from(fieldApiMetadataMap.keys());

    // Check each field with API configuration
    fieldIdsWithApiConfig.forEach(fieldId => {
      // Find the custom attribute for this field
      const customAttr = customAttributesList.find(attr => attr.id === fieldId);
      if (!customAttr) {
        return;
      }

      // Get the form control value
      const formControl = formGroup.get(customAttr.combinedAttributeName);
      const fieldValue = formControl?.value;

      // Only check validation if field has value
      const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

      if (hasValue) {
        const isValidated = fieldValidationStatus[fieldId]?.isValidated;
        const isSuccess = fieldValidationStatus[fieldId]?.isSuccess;

        // Field must be validated AND successful to be considered eligible
        if (!isValidated || !isSuccess) {
          pendingFields.push(customAttr.attributeName);
        }
      }
    });

    return pendingFields;
  }

  /**
   * Format date to YYYY-MM-DD format
   * @param date The date value to format
   * @returns Formatted date string or empty string
   */
  private formatDate(date: any): string {
    if (date == null || date === '') {
      return '';
    }

    const d = new Date(date);

    // Check if date is valid
    if (isNaN(d.getTime())) {
      return '';
    }

    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();

    return `${year}-${month}-${day}`;
  }
}
